import { normalizeSectorName } from './normalizeSectorName';
import type { BuildLodgeMarkerSectorNamesParams } from '../models/types';

const isRoyalVipMarkerSector = (sectorName: string | null | undefined): boolean => {
  if (!sectorName) return false;
  const normalized = sectorName
    .replace(/[–—_-]/g, ' ')
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return /^royal vip(?:\s*\d+)?$/i.test(normalized);
};

export const buildLodgeMarkerSectorNames = ({
  parsedSeats,
  sectorNames,
  normalizeSectorName,
}: BuildLodgeMarkerSectorNamesParams): Set<string> => {
  const names = new Set<string>();
  const outlinedSectors = new Set(
    sectorNames.filter(isRoyalVipMarkerSector).map(normalizeSectorName)
  );
  if (outlinedSectors.size === 0) return names;

  for (const seat of parsedSeats) {
    const name = normalizeSectorName(seat.sectorName);
    if (outlinedSectors.has(name)) {
      names.add(name);
    }
  }
  return names;
};

const VISIBILITY_NOTE_REGEX =
  /\(\s*возможное\s+ограничение\s+видимости\s+до\s+\d+(?:[.,]\d+)?\s*%\s*\)/gi;

const getSectorQualifier = (name: string) =>
  name
    .match(/(\(.*\))\s*$/)?.[1]
    .toLowerCase()
    .replace(VISIBILITY_NOTE_REGEX, '')
    .replace(/\s+/g, ' ')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .replace(/\)\s*\(/g, ') (')
    .trim();

const getQualifiedKey = (legacyName: string, qualifier: string) =>
  JSON.stringify([legacyName, qualifier]);

export const createSeatMapSectorNormalizer = (...sectorNameSources: string[][]) => {
  const qualifiedKeysByLegacyName = new Map<string, Set<string>>();
  const distinctBareNames = new Set<string>();

  for (const sectorNames of sectorNameSources) {
    const bareNames = new Set<string>();
    const qualifiedNames = new Set<string>();

    for (const name of sectorNames) {
      const legacyName = normalizeSectorName(name);
      const qualifier = getSectorQualifier(name);
      if (!qualifier) {
        bareNames.add(legacyName);
        continue;
      }

      qualifiedNames.add(legacyName);
      const keys = qualifiedKeysByLegacyName.get(legacyName) ?? new Set<string>();
      keys.add(getQualifiedKey(legacyName, qualifier));
      qualifiedKeysByLegacyName.set(legacyName, keys);
    }

    for (const name of bareNames) {
      if (qualifiedNames.has(name)) distinctBareNames.add(name);
    }
  }

  return (name?: string): string => {
    const legacyName = normalizeSectorName(name);
    if (!name) return legacyName;

    const qualifier = getSectorQualifier(name);
    if (qualifier) return getQualifiedKey(legacyName, qualifier);

    if (!distinctBareNames.has(legacyName)) {
      const keys = qualifiedKeysByLegacyName.get(legacyName);
      if (keys?.size === 1) return keys.values().next().value ?? legacyName;
    }

    return legacyName;
  };
};
